/**
 * Disambiguate Consumer Actor
 *
 * Handles consumer disambiguation when multiple matches are found.
 * Calls the disambiguate endpoint with the user's answer and returns the
 * same unified `MatchedConsumer` shape as `fetchConsumerActor` on success,
 * so the machine can reuse a single `assignMatchedConsumer` action for
 * every match transition.
 */

import {
  type DisambiguateRequest,
  disambiguate,
} from "@/app/directories/kiosk/server-actions/consumer-actions";
import { fetchUserEmail } from "@/app/directories/kiosk/server-actions/user-actions";
import { fromPromise } from "xstate";
import { convertBirthdayToISODate } from "./date.utils";
import type { MatchedConsumer } from "./fetch-consumer.actor";

export interface DisambiguateConsumerInput {
  phoneNumber: string;
  birthday: string;
  orgId: number;
  correlationId: string;
  disambiguationAnswer: Record<string, string>;
}

export interface DisambiguateConsumerOutput {
  message:
    | "PATIENT_MATCHED"
    | "MULTIPLE_PATIENTS_MATCHED"
    | "NO_PATIENTS_MATCHED";
  consumer?: MatchedConsumer;
  disambiguationField?: string;
  correlationId?: string;
}

/**
 * Core disambiguation logic — exported as a plain async function for direct
 * testing. Accepts a flat input with ISO-formatted dob and optional extra
 * disambiguation fields spread directly on the input.
 */
export async function disambiguateConsumerLogic({
  input,
}: {
  input: Record<string, unknown> & {
    phoneNumber: string;
    dob: string;
    orgId: number;
    correlationId: string;
  };
}): Promise<DisambiguateConsumerOutput> {
  const { phoneNumber, dob, orgId, correlationId, ...extraFields } = input;
  const normalizedPhone = phoneNumber.replace(/\D/g, "").slice(-10);

  try {
    const response = await disambiguate({
      phoneNumber: normalizedPhone,
      dob,
      orgId,
      correlationId,
      ...extraFields,
    } as DisambiguateRequest);

    const message = response.data.message;

    if (message === "PATIENT_MATCHED") {
      const consumer = response.data.consumer;
      let email: string | undefined;

      try {
        if (consumer?.userId) {
          const result = await fetchUserEmail(consumer.userId);
          email =
            result.status === "ok" && result.email ? result.email : undefined;
        }
      } catch {
        // Email fetch is best-effort
      }

      const matched: MatchedConsumer | undefined = consumer
        ? {
            id: consumer.id,
            userId: consumer.userId,
            firstname: consumer.firstname,
            lastname: consumer.lastname,
            dob: consumer.dob,
            email: email ?? consumer.email ?? undefined,
            insurances: consumer.insurances,
          }
        : undefined;

      const output: DisambiguateConsumerOutput = {
        message: "PATIENT_MATCHED",
        consumer: matched,
        correlationId: response.data.correlationId,
      };
      console.log("[disambiguateConsumerLogic] Response:", {
        message: output.message,
        hasConsumer: !!matched,
      });
      return output;
    }

    if (message === "MULTIPLE_PATIENTS_MATCHED") {
      const output: DisambiguateConsumerOutput = {
        message: "MULTIPLE_PATIENTS_MATCHED",
        disambiguationField: response.data.disambiguationField,
        correlationId: response.data.correlationId,
      };
      console.log("[disambiguateConsumerLogic] Response:", {
        message: output.message,
        hasConsumer: false,
      });
      return output;
    }

    const output: DisambiguateConsumerOutput = {
      message: "NO_PATIENTS_MATCHED",
      correlationId: response.data.correlationId,
    };
    console.log("[disambiguateConsumerLogic] Response:", {
      message: output.message,
      hasConsumer: false,
    });
    return output;
  } catch {
    const output: DisambiguateConsumerOutput = {
      message: "NO_PATIENTS_MATCHED",
    };
    console.log("[disambiguateConsumerLogic] Response:", {
      message: output.message,
      hasConsumer: false,
    });
    return output;
  }
}

/**
 * XState actor logic — converts the machine's context-shaped input to the
 * flat format expected by `disambiguateConsumerLogic`.
 */
export const disambiguateConsumerActor = fromPromise<
  DisambiguateConsumerOutput,
  DisambiguateConsumerInput
>(async ({ input }) => {
  const isoDate = convertBirthdayToISODate(input.birthday);
  if (!isoDate) {
    // Mirror `fetchConsumerActor`: without a parseable birthday we can't
    // reliably disambiguate, so report "no match" and let the machine
    // fall through to the new-consumer path instead of crashing.
    console.warn(
      "[disambiguateConsumerActor] Skipping disambiguation — invalid/empty birthday:",
      input.birthday,
    );
    return { message: "NO_PATIENTS_MATCHED" };
  }
  return disambiguateConsumerLogic({
    input: {
      phoneNumber: input.phoneNumber,
      dob: isoDate,
      orgId: input.orgId,
      correlationId: input.correlationId,
      ...input.disambiguationAnswer,
    },
  });
});
